import React from 'react';
import { motion } from 'motion/react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../lib/utils';
import { XOVRLabel } from './XOVRLabel';

interface Props {
  breakdown: any;
  ovr: number;
}

export const XOVRBreakdown: React.FC<Props> = ({ breakdown, ovr }) => {
  if (!breakdown || breakdown.confidenceLevel === 'none') return null;

  return (
    <div className="bg-canvas rounded-[2rem] border border-hairline p-6 space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest"><XOVRLabel /> Breakdown</p>
        <div className={cn(
          "px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest",
          breakdown.confidenceLevel === 'high'   ? "bg-canvas text-accent-ink" :
          breakdown.confidenceLevel === 'medium' ? "bg-gold-soft text-gold-ink" :
          "bg-raised text-ink-2"
        )}>
          {breakdown.confidenceLabel}
        </div>
      </div>

      {/* Delta visualization */}
      <div className="flex items-center space-x-6">
        <div className="text-center">
          <div className="text-2xl font-black text-ink-2">{breakdown.scoutingOVR}</div>
          <div className="text-[8px] font-semibold text-ink-2 uppercase tracking-widest">Scout</div>
        </div>

        <div className="flex-1 space-y-3">
          <div className="flex justify-between items-end">
            <span className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Stat Impact</span>
            <span className={cn(
              "text-sm font-black italic",
              breakdown.statDelta > 0 ? "text-accent-ink" :
              breakdown.statDelta < 0 ? "text-danger" : "text-ink-2"
            )}>
              {breakdown.statDelta > 0 ? `+${breakdown.statDelta}` :
               breakdown.statDelta < 0 ? `${breakdown.statDelta}` : '±0'}
            </span>
          </div>
          {/* Horizontal bar with center anchor */}
          <div className="h-1.5 bg-raised rounded-full relative overflow-hidden">
            <div 
              className={cn(
                "absolute top-0 bottom-0 transition-all duration-700",
                breakdown.statDelta > 0 ? "bg-ink left-1/2" : "bg-red-500 right-1/2"
              )}
              style={{ 
                width: `${Math.min(50, (Math.abs(breakdown.statDelta) / 15) * 50)}%` 
              }}
            />
          </div>
        </div>

        <div className="text-center">
          <div className={cn(
            "text-3xl font-black italic",
            breakdown.statDelta > 0 ? "text-accent-ink drop-shadow-[0_0_10px_rgba(52,211,153,0.3)]" :
            breakdown.statDelta < 0 ? "text-danger" : "text-ink"
          )}>
            {ovr || 0}
          </div>
          <div className="text-[8px] font-semibold text-ink-2 uppercase tracking-widest">Blended</div>
        </div>
      </div>

      {/* Signal Details */}
      <div className="space-y-3 pt-4 border-t border-hairline">
        <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">Driving Signals</p>
        <div className="grid grid-cols-2 gap-3">
          {breakdown.impact?.map((imp: any) => (
            <div key={imp.label} className="bg-surface p-3 rounded-2xl flex items-center justify-between border border-hairline">
              <span className="text-[10px] font-bold text-ink-2 uppercase tracking-tight truncate mr-2">{imp.label}</span>
              <div className="flex items-center space-x-1">
                {imp.value > 0 ? <TrendingUp className="w-2.5 h-2.5 text-accent-ink" /> : <TrendingDown className="w-2.5 h-2.5 text-danger" />}
                <span className={cn(
                  "text-[10px] font-black",
                  imp.value > 0 ? "text-accent-ink" : "text-danger"
                )}>
                  {imp.value > 0 ? `+${imp.value}` : imp.value}
                </span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[9px] text-ink-2 italic leading-relaxed bg-surface p-3 rounded-xl border border-hairline">
          {breakdown.signalType === 'batting' 
            ? "Batting stats are heavily weighted on OPS. K% acts as a secondary penalty for high strikeout rates."
            : "Pitching stats are anchored on WHIP, with ERA serving as a secondary performance modifier."
          }
          {" "}
          {breakdown.confidenceLevel === 'low' && "Confidence is currently low due to small sample size."}
        </p>
      </div>
    </div>
  );
};
